import type {Sorting} from '@igp/shared/api';
import {useAuthUser} from '@igp/shared/auth';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import type {PlayerEcologyWeeklyReport} from '@igp/shared/contract/pam';
import type {UseListViewEntityProps, UseListViewEntityResult} from '@igp/shared/redux/view';
import {useColumnsSettings} from '@igp/shared/user-settings';
import {momentToTimestampSeconds} from '@igp/shared/utils';
import {EntityType} from '@models';
import {useViewInit} from '@redux';

import {
    firstAvailableDate,
    PlayerEcologyWeeklyQueryFieldsTypeName,
    PlayerEcologyWeeklyReportTypeName,
    PlayerEcologyWeeklySortKeysTypeName,
    PlayerEcologyWeeklyViewModelKeysTypeName,
    PlayerEcologyWeeklyViewModelTypeName,
} from './constants';
import type {
    PlayerEcologyWeeklyColumns,
    PlayerEcologyWeeklyColumnSettings,
    PlayerEcologyWeeklyFilterKeys,
    PlayerEcologyWeeklyQueryFields,
    PlayerEcologyWeeklySortKeys,
    PlayerEcologyWeeklyViewModel,
    PlayerEcologyWeeklyViewModelKeys,
} from './types';

type UsePlayerEcologyWeeklyProps = Pick<
    UseListViewEntityProps<PlayerEcologyWeeklyFilterKeys, PlayerEcologyWeeklyViewModelKeys>,
    | 'viewType'
    | 'fields'
    | 'cleanDelay'
    | 'defaultFilters'
    | 'defaultPaging'
    | 'defaultSorting'
    | 'syncWithUrl'
    | 'validateFilter'
    | 'displayName'
    | 'hiddenFilters'
>;

export function usePlayerEcologyWeekly({
    viewType,
    fields,
    cleanDelay,
    defaultFilters,
    defaultPaging,
    defaultSorting,
    syncWithUrl,
    validateFilter,
    displayName,
}: UsePlayerEcologyWeeklyProps): UseListViewEntityResult<PlayerEcologyWeeklyViewModel, PlayerEcologyWeeklyViewModelKeys> {
    const mapper = useAutoMapper();
    const queryFields = fields.map(f =>
        mapper.map<PlayerEcologyWeeklyViewModelKeys, PlayerEcologyWeeklyQueryFields>(
            f,
            PlayerEcologyWeeklyViewModelKeysTypeName,
            PlayerEcologyWeeklyQueryFieldsTypeName
        )
    );

    const {
        items: serverItems,
        totalCount,
        viewEntity,
        getSearchFilter,
        handleSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    } = useViewInit<PlayerEcologyWeeklyReport, PlayerEcologyWeeklyFilterKeys, PlayerEcologyWeeklyQueryFields, PlayerEcologyWeeklySortKeys>({
        viewType,
        entity: {entity: EntityType.PlayerEcologyWeekly, fields: queryFields},
        cleanDelay,
        defaultFilters,
        hiddenFilters: [{key: 'report_week_date_min', value: momentToTimestampSeconds(firstAvailableDate)}],
        defaultPaging,
        defaultSorting: mapSorting(defaultSorting ?? [{field: 'playerUid', sort: 'desc'}]),
        syncWithUrl,
        validateFilter,
        displayName,
    });

    const items = serverItems.map(i =>
        mapper.tryMap<PlayerEcologyWeeklyReport, PlayerEcologyWeeklyViewModel>(
            i,
            PlayerEcologyWeeklyReportTypeName,
            PlayerEcologyWeeklyViewModelTypeName
        )
    );
    const searchFilter = getSearchFilter(i =>
        mapper.map<PlayerEcologyWeeklySortKeys, PlayerEcologyWeeklyViewModelKeys>(
            i,
            PlayerEcologyWeeklySortKeysTypeName,
            PlayerEcologyWeeklyViewModelKeysTypeName
        )
    );

    function mapSorting(sorting: Sorting<PlayerEcologyWeeklyViewModelKeys>[]): Sorting<PlayerEcologyWeeklySortKeys>[] {
        return (
            sorting?.map(s => ({
                field: mapper.map<PlayerEcologyWeeklyViewModelKeys, PlayerEcologyWeeklySortKeys>(
                    s.field,
                    PlayerEcologyWeeklyViewModelKeysTypeName,
                    PlayerEcologyWeeklySortKeysTypeName
                ),
                sort: s.sort,
            })) ?? []
        );
    }

    function handleVmSortChange(sorting: Sorting<PlayerEcologyWeeklyViewModelKeys>[]) {
        handleSortChange(mapSorting(sorting));
    }

    return {
        items: items?.map(i => i.model),
        rawItems: items?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        totalCount,
        searchFilter,
        filterString: viewEntity?.filter,
        handleSortChange: handleVmSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    };
}

type UsePlayerEcologyWeeklyColumnSettingsProps = {
    gridId: string;
    defaultColumns: PlayerEcologyWeeklyColumns[];
    pinnedColumns?: PlayerEcologyWeeklyColumns[];
};

type UsePlayerEcologyWeeklyColumnSettingsResult = {
    visibleColumns?: PlayerEcologyWeeklyColumns[];
    onVisibleColumnsChange: (value: PlayerEcologyWeeklyColumns[]) => void;
};

export function usePlayerEcologyWeeklyColumnSettings({
    gridId,
    defaultColumns,
    pinnedColumns,
}: UsePlayerEcologyWeeklyColumnSettingsProps): UsePlayerEcologyWeeklyColumnSettingsResult {
    const {sub} = useAuthUser();
    const {visible, onVisibleColumnsChange} = useColumnsSettings<PlayerEcologyWeeklyColumnSettings>({
        userId: sub,
        settingId: gridId,
        defaultColumns,
        pinnedColumns,
    });

    function handleColumnSettingsChange(columns: PlayerEcologyWeeklyColumns[]): void {
        onVisibleColumnsChange(columns);
    }

    return {
        visibleColumns: visible,
        onVisibleColumnsChange: handleColumnSettingsChange,
    };
}
